"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { isFormula } from '@membranehq/sdk';
import { isDefinedValue } from '../ComboBox/Badges/isDefinedValue.mjs';
import { useState, useEffect } from 'react';
import '../DataBuilderUI/FieldControlPanel/index.mjs';
import '../DataBuilderUI/ActionBar.mjs';
import { Cell } from '../DataBuilderUI/Cell.mjs';
import { NestedLevel } from '../DataBuilderUI/NestedLevel.mjs';
import { Row } from '../DataBuilderUI/Row.mjs';
import '../DataBuilderUI/Title.mjs';
import { isFieldVisible } from './data-field.mjs';
import { DataBuilderFieldProvider } from './field-context.mjs';
import { FieldCopy } from './FieldCopy.mjs';
import { FieldDelete } from './FieldDelete.mjs';
import { FieldName } from './FieldName/index.mjs';
import { FieldTypeSelector } from './FieldTypeSelector/index.mjs';
import { convertableSchemaToSchema } from './FieldTypeSelector/convertableSchemaToSchema.mjs';
import { useIsFieldFrozen } from './hooks.mjs';
import { RedactedToggleButton } from './RedactedToggleButton.mjs';
import useDataBuilder from './use-data-builder.mjs';
import { useDataBuilderComponents } from './use-data-builder-components.mjs';
import useDataBuilderField from './use-data-builder-field.mjs';

const fieldCopyRowHoverStyles = "[&:hover_.field-copy-button]:opacity-100";
function DataBuilderFormField({ field, onChange, onKeyChange, onDelete, onEnter }) {
  const { hideReadOnlyFields } = useDataBuilder();
  if (field.schema?.readOnly && hideReadOnlyFields) {
    return null;
  }
  return /* @__PURE__ */ jsx(DataBuilderFieldProvider, { field, children: /* @__PURE__ */ jsx(DataBuilderFieldContent, { onChange, onKeyChange, onDelete, onEnter }) });
}
function DataBuilderFieldContent({ onChange, onKeyChange, onDelete, onEnter }) {
  const { Nested } = useDataBuilderComponents();
  const { isReadOnly } = useDataBuilder();
  const isFieldFrozen = useIsFieldFrozen();
  const {
    field,
    localSchema,
    setLocalSchema,
    resetLocalSchema,
    localSchemaChangedManually,
    setLocalSchemaChangedManually,
    autoOpen,
    setAutoOpen
  } = useDataBuilderField();
  const [isRedacted, setIsRedacted] = useState(localSchema.isSensitive);
  useEffect(() => {
    if (!localSchemaChangedManually) {
      resetLocalSchema();
    }
  }, [JSON.stringify(field?.schema)]);
  function handleChange(value) {
    onChange?.(value);
  }
  function handleKeyChange(newKey) {
    onKeyChange?.(newKey);
  }
  function handleEnterKey(value) {
    if (value && onEnter) {
      onEnter?.();
    }
  }
  function handleEscKey(value) {
    if (!value && onDelete) {
      onDelete?.();
    }
  }
  function handleLocalSchemaTypeChange(schemaType) {
    const showValueDropdown = !(schemaType === "object" || schemaType === "array");
    const resetValue = !convertableSchemaToSchema(localSchema?.type, schemaType);
    setLocalSchemaChangedManually(true);
    setLocalSchema({ ...localSchema, type: schemaType });
    if (resetValue) {
      onChange?.("");
    }
    setAutoOpen(showValueDropdown);
  }
  let showValue = true;
  const fieldIsObjectOrArray = ["object", "array"].includes(localSchema?.type);
  const fieldHasCustomValue = !isFormula(field?.value);
  if (fieldIsObjectOrArray && fieldHasCustomValue && !autoOpen) {
    showValue = false;
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(NestedLevel, { level: field.level, children: isFieldVisible(field) && /* @__PURE__ */ jsxs(
      Row,
      {
        span: !showValue ? "short" : "default",
        className: field.schema?.readOnly ? fieldCopyRowHoverStyles : void 0,
        children: [
          (isDefinedValue(field.key) || field.schema?.title) && /* @__PURE__ */ jsx(Cell, { type: "name", children: /* @__PURE__ */ jsx(
            FieldName,
            {
              field,
              onKeyChange: handleKeyChange,
              onEnter: handleEnterKey,
              onEsc: handleEscKey,
              typeSelectorSlot: /* @__PURE__ */ jsx(FieldTypeSelector, { onChange: handleChange, onSchemaChange: handleLocalSchemaTypeChange })
            }
          ) }),
          showValue && /* @__PURE__ */ jsxs(Cell, { type: "combobox", span: "grow", "data-field-redacted": isRedacted, className: "ph-mask", children: [
            /* @__PURE__ */ jsx(FieldDataInput, { onChange: handleChange }),
            localSchema.isSensitive && /* @__PURE__ */ jsx(
              RedactedToggleButton,
              {
                visible: !isRedacted,
                onClick: () => {
                  setIsRedacted((val) => !val);
                }
              }
            ),
            field.schema?.readOnly && /* @__PURE__ */ jsx(FieldCopy, { value: field.value })
          ] }),
          field.isDeletable && !isReadOnly && !isFieldFrozen && !field.schema?.readOnly && /* @__PURE__ */ jsx(FieldDelete, { onClick: onDelete })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(Nested, { field, onChange: handleChange })
  ] });
}
function FieldDataInput({ onChange }) {
  const { FieldValue } = useDataBuilderComponents();
  const { field, localSchema, setLocalSchemaChangedManually } = useDataBuilderField();
  function handleChange(value) {
    setLocalSchemaChangedManually(false);
    onChange(value);
  }
  const fieldWithDefinedSchema = !field.schema?.type ? {
    ...field,
    schema: {
      ...field.schema,
      ...localSchema,
      anyOf: void 0
    }
  } : field;
  return /* @__PURE__ */ jsx(FieldValue, { field: fieldWithDefinedSchema, onChange: handleChange });
}

export { DataBuilderFormField };
